import React from 'react';
import { groupFields } from './fieldLabels';

// AllFieldsPanel — the shared "every field NetSuite has" panel (tasks #62/#60).
// Drop it onto any native record page:
//
//   <AllFieldsPanel family="transaction" nsRaw={record.ns_raw}
//                   overrides={{ shipmethod: record.ship_method }} />
//
// Renders the record's captured ns_raw (the full SuiteQL row, custom fields
// included) as grouped label/value rows from the static map in
// fieldLabels.js. Promoted mirror columns override their raw twin and are
// tagged, so the typed/joined value wins over the bare NS internal id.
// Honest empty state while the 20260705 capture migrations/backfill haven't
// run yet. Read-only; styling reuses the scoped .nit-* classes.

function fmtValue(v) {
  if (v === 'T' || v === true) return 'Yes';
  if (v === 'F' || v === false) return 'No';
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function AllFieldsPanel({ family, nsRaw, overrides = {}, title = 'All fields' }) {
  const groups = groupFields(family, nsRaw, overrides);
  const fieldCount = groups.reduce((s, g) => s + g.fields.length, 0);

  return (
    <div className="nit-card">
      <div className="nit-hist-head">
        <h3>{title}</h3>
        {fieldCount > 0 && <span className="nit-muted">{fieldCount} populated fields from the NetSuite mirror</span>}
      </div>

      {(!nsRaw || fieldCount === 0) && (
        <div className="nit-empty">
          Full-field capture hasn&apos;t reached this record yet — <code>ns_raw</code> is empty.
          <br />
          Once the 20260705 capture migration is applied and the owning sync runs, every
          SuiteQL-visible field (standard and custom) appears here automatically. Until then the
          promoted columns on the other tabs are what the mirror carries.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.group} style={{ marginBottom: 14 }}>
          <h4 className="nit-muted" style={{ margin: '10px 0 6px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {g.group}
          </h4>
          <div className="nit-table-wrap">
            <table className="nit-table">
              <tbody>
                {g.fields.map((f) => (
                  <tr key={f.key}>
                    <td style={{ width: '34%' }}>
                      {f.label}
                      {f.promoted && (
                        <span className="nit-tag" style={{ marginLeft: 6 }} title="Promoted mirror column (typed/labeled) shown instead of the raw NetSuite value">
                          promoted
                        </span>
                      )}
                    </td>
                    <td className="nit-muted" style={{ width: '22%' }}><code>{f.key}</code></td>
                    <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fmtValue(f.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {fieldCount > 0 && (
        <p className="nit-caption">
          Values come straight from the last synced SuiteQL row (explicit column list — the audit
          proved SELECT * hides populated columns). NS-internal-id fields show the raw id unless a
          promoted column carries the label.
        </p>
      )}
    </div>
  );
}
